/**
 * Quantity and price entry, on the custom keypad.
 *
 * One component for both because they are the same interaction with a different label and a
 * different validity rule — and because two near-identical keypad sheets would drift.
 */

import { useState } from 'react';

import { formatKes, parseKes } from '@pos/shared';

import { Button, InlineError, Keypad, Sheet } from '@/shared/ui';

export type QuantitySheetProps = {
  open: boolean;
  mode: 'quantity' | 'price';
  title: string;
  /** The current value, as digits. */
  initial: number;
  /** Rejects a value with a reason. Returning null accepts it. */
  validate?: (value: number) => string | null;
  /**
   * Says something about a value without refusing it — a price under the lowest one a salesperson
   * may give, which still goes ahead and is sent to a manager at payment.
   */
  notice?: (value: number) => string | null;
  onCommit: (value: number) => void;
  onDismiss: () => void;
};

export function QuantitySheet({
  open,
  mode,
  title,
  initial,
  validate,
  notice,
  onCommit,
  onDismiss,
}: QuantitySheetProps) {
  // Starts empty rather than pre-filled: the first digit typed replaces the value, which is what
  // every till in the world does and what the muscle memory expects.
  const [digits, setDigits] = useState('');
  const [error, setError] = useState<string | null>(null);

  const value = digits.length === 0 ? initial : (parseKes(digits) ?? 0);
  const note = error === null && digits.length > 0 ? (notice?.(value) ?? null) : null;

  const commit = () => {
    const message = validate?.(value) ?? null;
    if (message !== null) {
      setError(message);
      return;
    }

    onCommit(value);
    setDigits('');
    setError(null);
  };

  const dismiss = () => {
    setDigits('');
    setError(null);
    onDismiss();
  };

  return (
    <Sheet
      open={open}
      onDismiss={dismiss}
      title={title}
      footer={
        <>
          <Button variant="outline" fullWidth onClick={dismiss}>
            Cancel
          </Button>
          <Button fullWidth onClick={commit}>
            Done
          </Button>
        </>
      }
    >
      <div className="flex flex-col items-center gap-5">
        <p
          className="text-4xl font-bold tracking-tight text-content-primary"
          data-nums="money"
          aria-live="polite"
        >
          {mode === 'price' ? formatKes(value) : value}
        </p>

        {digits.length === 0 && <p className="-mt-3 text-xs text-content-muted">Type to replace</p>}

        {error !== null && <InlineError message={error} />}

        {note !== null && (
          <p
            role="status"
            className="w-full max-w-[19rem] rounded-md border border-warning-border bg-warning-subtle px-3 py-2 text-sm font-medium text-warning-subtle-text"
          >
            {note}
          </p>
        )}

        <Keypad
          className="w-full max-w-[19rem]"
          extraKey={mode === 'price' ? '00' : null}
          onDigit={(digit) => {
            setError(null);
            // Capped so a stuck key cannot produce a sixteen-digit price that overflows the
            // receipt and the server's `long` at the same time.
            setDigits((current) => (current.length >= 9 ? current : current + digit));
          }}
          onAction={(action) => {
            setError(null);
            setDigits((current) => (action === 'clear' ? '' : current.slice(0, -1)));
          }}
        />
      </div>
    </Sheet>
  );
}
